import React, { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import styled from 'styled-typed'
import { BaseRouteParams } from 'hocs'
import { useHasFeature, useHasFeatureFunc } from 'utils/useHasFeature'
import { useTranslation, Trans } from 'translations'

const GuidelinesLink = styled.a`
    color: ${(props) => props.theme.colors.boyBlue};
`

export interface BookingNavigationSection {
    path: string
    active: boolean
    shouldShow: boolean
    tabTitle: string
    title: string
    description: string
    secondDescription?: string
    guidelinesSubject?: string
    tabHeader?: React.ReactNode
}

export const useBookingNavigation = () => {
    const { pathname, search } = useLocation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const reservationPoolsOn = useHasFeature({ featureName: 'RetailersReservationCodes' })
    const hasFeature = useHasFeatureFunc()
    const { t } = useTranslation()

    return useMemo(() => {
        const sections = [
            {
                path: `/account/${accountSlug}/venue/codes/tickets/list/`,
                active: pathname.includes('/tickets'),
                shouldShow: true,
                tabTitle: t('venue.bookingCodes.navigation.tickets.title'),
                title: t('venue.bookingCodes.navigation.tickets.title'),
                description: t('venue.bookingCodes.navigation.tickets.description'),
                secondDescription: '',
                guidelinesSubject: 'barcodes',
            },
            {
                path: `/account/${accountSlug}/venue/codes/reservations/${reservationPoolsOn ? 'list/' : ''}`,
                active: pathname.includes('/reservations'),
                shouldShow: hasFeature('BarcodeCheck'),
                tabTitle: t('venue.bookingCodes.navigation.reservations.title'),
                title: t('venue.bookingCodes.navigation.reservations.title'),
                description: t('venue.bookingCodes.navigation.reservations.description'),
                secondDescription: (
                    <div>
                        <Trans
                            i18nKey="venue.bookingCodes.navigation.reservations.note"
                            components={{
                                anchor: (
                                    <GuidelinesLink
                                        target="_blank"
                                        href="https://support.convious.com/help/how-to-upload-reservation-codes-in-your-control-panel"
                                    />
                                ),
                            }}
                        />
                    </div>
                ),
            },
            {
                path: `/account/${accountSlug}/venue/codes/sold_tickets/`,
                active: pathname.includes('/codes/sold_tickets'),
                shouldShow: hasFeature('SeasonPassesUploadFeature'),
                tabTitle: t('venue.bookingCodes.navigation.seasonPasses.title'),
                title: t('venue.bookingCodes.navigation.seasonPasses.title'),
                description: t('venue.bookingCodes.navigation.seasonPasses.description'),
            },
            {
                path: `/account/${accountSlug}/venue/codes/product_ids/`,
                active: pathname.includes('/codes/product_ids'),
                shouldShow: true,
                tabTitle: t('venue.bookingCodes.navigation.productIds.title'),
                title: t('venue.bookingCodes.navigation.productIds.title'),
                description: t('venue.bookingCodes.navigation.productIds.description'),
            },
        ] as BookingNavigationSection[]

        return sections.filter((section) => section.shouldShow)
    }, [accountSlug, pathname, search, t])
}
